import {
  Dialog,
  DialogContent,
  DialogProps,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import React from 'react'
import { FormProvider } from 'react-hook-form'
import { EditModeProvider } from 'contexts/edit-mode.context'
import AddDriverForm from '../forms/add-driver-form/add-driver-form.component'
import { AddDriverButton } from './add-driver-dialog.styles'
import { useAddDriverForm } from '../forms/add-driver-form/add-driver-form.hooks'
import {
  useAddDriverMutation,
  useListDriversQuery,
} from '../../store/drivers/drivers.api'
import { AddDriverPayload } from '../../store/drivers/drivers.type'
import { setSelectedDriver } from 'store/drivers/drivers.slice'
import { useDispatch } from 'react-redux'
import { getUserId } from 'utils/common'
import useSubmitLock from 'hooks/use-submit-lock'

type AddDriverDialogProps = {
  handleClose: () => void
} & DialogProps

const AddDriverDialog = ({ open, handleClose }: AddDriverDialogProps) => {
  const formMethods = useAddDriverForm()
  const dispatch = useDispatch()
  const userId = getUserId()
  const { isSubmitting, wrapFormSubmit } = useSubmitLock()

  const [addDriver, { isError }] = useAddDriverMutation()
  const { refetch } = useListDriversQuery({ userId }, { skip: !userId })

  const handleAdd = async (data: AddDriverPayload) => {
    const { data: driver } = await addDriver({
      firstName: '',
      lastName: '',
      phone: data.phone,
      owner: {
        id: window.sessionStorage.getItem('userId') ?? '',
        companyAddress: '',
        companyName: '',
        email: '',
        password: '',
      },
    })
    if (!isError) handleClose()
    refetch()
    if (!!driver) dispatch(setSelectedDriver(driver))
  }

  return (
    <Dialog open={open} maxWidth="lg">
      <DialogContent sx={{ p: 8, borderRadius: 8, textAlign: 'center', position: 'relative' }}>
        <IconButton
          onClick={handleClose}
          disableRipple
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'error.main',
            width: 40,
            height: 40,
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&:active': {
              backgroundColor: 'transparent',
            },
            '& .MuiTouchRipple-root': {
              display: 'none',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 50 }} />
        </IconButton>
        <FormProvider {...formMethods}>
          <form
            onSubmit={wrapFormSubmit(formMethods.handleSubmit, handleAdd)}
          >
            <Stack spacing={2}>
              <Typography style={{ fontSize: 40, fontWeight: 300 }}>
                Please enter the phone number
              </Typography>
              <EditModeProvider initialEditMode suppressAutofill>
                <AddDriverForm />
              </EditModeProvider>
              <AddDriverButton
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled',
                  },
                }}
              >
                Add
              </AddDriverButton>
            </Stack>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

export default AddDriverDialog
